<template>
  <SwitchOptions is-collection />

  <template v-if="collectionStore.selectedEndProductOption">
    <CollectionDropdown
      v-model="collectionStore.selectedEndProductOption"
      :label="$t('pages.collection.size.label')"
      :options="collectionStore.endProductOptions"
      show-prices
      @update:model-value="onEndProductChange"
    />
  </template>

  <InStock />

  <PriceDisplay
    v-bind="{
      basePrice,
      finalPrice,
    }"
  />

  <StickyBottomButton>
    <DefaultButton
      id="order-button"
      class="focus:outline-none"
      @click="handleButtonClick"
    >
      <template #icon>
        <SpinnerIcon v-if="loading" class="h-6 w-6 text-white" />
        <CartIcon v-else class="h-11 py-2 md:h-8 md:py-1" />
      </template>

      {{ $t('pages.extra_options.add_to_cart') }}
    </DefaultButton>
  </StickyBottomButton>

  <UspItems />

  <DeliveryOptions />

  <Transition
    enter-active-class="transition ease-in duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <ExtraOptionsModal v-if="collectionStore.extraOptionsModalOpen" />
  </Transition>
</template>

<script lang="ts" setup>
import StickyBottomButton from '@/components/Buttons/StickyBottomButton.vue';
import CartIcon from '@/components/Icons/CartIcon.vue';
import { useAddToCart } from '@/composables/UseAddToCart';
import { useExtraOptions } from '@/composables/UseExtraOptions';
import { useCollectionStore } from '@/stores/Collection/CollectionStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { ConfigurableCollectionProductObject } from '@/types/joostconnector';
import _ from 'lodash';
import {
  computed,
  type ComputedRef,
  onBeforeMount,
  onMounted,
  ref,
  Ref,
  watch,
} from 'vue';
import DeliveryOptions from './partials//ProductDetailPage/DeliveryOptions.vue';
import ExtraOptionsModal from './partials/Collection/ExtraOptionsModal.vue';
import CollectionDropdown from './partials/Collection/Filters/CollectionDropdown.vue';
import InStock from './partials/ProductDetailPage/InStock.vue';
import PriceDisplay from './partials/ProductDetailPage/PriceDisplay.vue';
import UspItems from './partials/ProductDetailPage/UspItems.vue';
import { SelectorOption } from '@/types/Configurator/SelectorOption';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import type { PaazlDeliveryOptionsRequestObject } from '@/types/Paazl/PaazlDeliveryOptionsRequestObject.ts';
import { usePaazlStore } from '@/stores/Paazl/PaazlStore.ts';
import SwitchOptions from '@/pages/ProductPage/partials/Collection/SwitchOptions.vue';
import { watchOnce } from '@vueuse/core';
import SpinnerIcon from '@/components/Icons/SpinnerIcon.vue';

const collectionStore = useCollectionStore();
const extraOptions = useExtraOptions();
const extraOptionsStore = useExtraOptionsStore();
const addToCart = useAddToCart();
const paazlStore = usePaazlStore();

const paazlDeliveryOptionsRequestObject: ComputedRef<PaazlDeliveryOptionsRequestObject> =
  computed(() => ({
    sku: collectionStore.selectedEndProductValue!.sku,
    shipping_cutoff_time: collectionStore.selectedEndProductValue!
      .productAttributes.shippingCutoffTime
      ? (collectionStore.selectedEndProductValue!.productAttributes
          .shippingCutoffTime.value as string)
      : undefined,
    processing_days: Number(
      collectionStore.selectedEndProductValue!.productAttributes.processingDays
        ? (collectionStore.selectedEndProductValue!.productAttributes
            .processingDays.value as number)
        : 0
    ),
    width: Number(
      collectionStore.selectedEndProductValue!.productAttributes.width
        .value as number
    ),
    height: Number(
      collectionStore.selectedEndProductValue!.productAttributes.height
        .value as number
    ),
    paazl_delivery_matrix: collectionStore.selectedEndProductValue!
      .productAttributes.paazlDeliveryMatrix
      ? (collectionStore.selectedEndProductValue!.productAttributes
          .paazlDeliveryMatrix.value as string)
      : undefined,
    product_price: Number(
      collectionStore.selectedEndProductValue!.price as number
    ),
  }));

const basePrice: Ref<number | undefined> = ref(
  (collectionStore.selectedEndProductValue!.recommendedPrice ??
    window.product_prices.base_price) / 100
);
const finalPrice: Ref<number> = ref(
  (collectionStore.selectedEndProductValue!.price ??
    window.product_prices.final_price) / 100
);

const loading = computed(
  () =>
    addToCart.isAwaitingRedirect.value ||
    addToCart.addCollectionProductToCartRequest.isLoading ||
    extraOptions.request.isLoading
);

type ImageObject = {
  thumb: string;
  full: string;
  img: string;
  caption?: string;
  position: number;
  isMain: boolean;
  type: string;
  videoUrl: null;
};

function handleButtonClick(): void {
  if (loading.value) {
    watchOnce(loading, () => {
      handleButtonClick();
    });
    return;
  }

  // Open modal and lock background scrolling
  if (extraOptionsStore.productOptionsCategories.length > 0) {
    collectionStore.extraOptionsModalOpen = true;
    return;
  }

  addToCart.addCollectionProductToCart(
    collectionStore.selectedEndProductValue!
  );
}

function updateImageGallery(): void {
  const selectedSize = collectionStore.selectedEndProductValue!;

  const imagesArray: ImageObject[] = [
    {
      thumb: selectedSize.defaultImage.thumb,
      full: selectedSize.defaultImage.full,
      img: selectedSize.defaultImage.img,
      position: 0,
      isMain: true,
      type: 'image',
      videoUrl: null,
    },
  ];

  for (
    let i = 0;
    i < Math.min(_.toLength(selectedSize.images.length), 6);
    i++
  ) {
    imagesArray.push({
      thumb: selectedSize.images[i].thumb,
      full: selectedSize.images[i].full,
      img: selectedSize.images[i].img,
      position: i + 1,
      isMain: false,
      type: 'image',
      videoUrl: null,
    });
  }

  window.dispatchEvent(
    new CustomEvent('update-gallery', { detail: imagesArray })
  );
}

function requestExtraOptions(): void {
  extraOptions
    .requestCategories(collectionStore.selectedEndProductValue!.sku, true)
    .then(extraOptionsStore.selectDefaultProductOptions);
}

function onEndProductChange(endProduct: SelectorOption): void {
  basePrice.value = endProduct.recommendedPrice
    ? endProduct.recommendedPrice / 100
    : undefined;
  finalPrice.value = endProduct.price
    ? endProduct.price / 100
    : window.product_prices.final_price / 100;

  paazlStore.requestShippingOptions(paazlDeliveryOptionsRequestObject.value);
}

watch(
  () => collectionStore.selectedEndProductValue,
  (newValue: ConfigurableCollectionProductObject | undefined) => {
    if (!newValue) {
      return;
    }

    // Update the image gallery
    updateImageGallery();

    // Do nothing if the sku hasn't changed
    if (newValue.sku === collectionStore.previousSku) {
      return;
    }

    // Set new sku as previous one
    collectionStore.previousSku = newValue.sku;

    // Request extra options
    requestExtraOptions();
  }
);

onMounted(() => {
  // Request extra options
  requestExtraOptions();

  // Update the image gallery
  setTimeout(updateImageGallery, 0);
});

onBeforeMount(() => {
  paazlStore.requestShippingOptions(paazlDeliveryOptionsRequestObject.value);
});
</script>
